import {useContext, useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {Box, Typography} from '@mui/material';

import {EmptyStateCard} from '../../../../components/EmptyStateCard/EmptyStateCard';
import {ModulePageHeader} from '../../../../components/ModulePageHeader/ModulePageHeader';
import {CompanyContext} from '../../../../contexts/CompanyContext/CompanyContext';
import {DepartmentHealthBenchmarkTable} from '../../../../features/company/workforce/components/DepartmentHealth/DepartmentHealthBenchmarkTable';
import {DepartmentHealthBenchmarkTableSkeleton} from '../../../../features/company/workforce/components/DepartmentHealth/DepartmentHealthBenchmarkTableSkeleton';
import {DepartmentHealthDataFreshness} from '../../../../features/company/workforce/components/DepartmentHealth/DepartmentHealthDataFreshness';
import {DepartmentHealthNarrativeHeader} from '../../../../features/company/workforce/components/DepartmentHealth/DepartmentHealthNarrativeHeader';
import {DepartmentHealthNarrativeHeaderSkeleton} from '../../../../features/company/workforce/components/DepartmentHealth/DepartmentHealthNarrativeHeaderSkeleton';
import {DepartmentHealthProfileDecisionGuide} from '../../../../features/company/workforce/components/DepartmentHealth/DepartmentHealthProfileDecisionGuide';
import {DepartmentHealthRecommendedActions} from '../../../../features/company/workforce/components/DepartmentHealth/DepartmentHealthRecommendedActions';
import {DepartmentHealthRoleCoverageHealth} from '../../../../features/company/workforce/components/DepartmentHealth/DepartmentHealthRoleCoverageHealth';
import {DepartmentHealthScoreGrid} from '../../../../features/company/workforce/components/DepartmentHealth/DepartmentHealthScoreGrid';
import {DepartmentHealthSkillCoverageMap} from '../../../../features/company/workforce/components/DepartmentHealth/DepartmentHealthSkillCoverageMap';
import {DepartmentSelectorBar} from '../../../../features/company/workforce/components/DepartmentHealth/DepartmentSelectorBar';
import {DepartmentSelectorBarSkeleton} from '../../../../features/company/workforce/components/DepartmentHealth/DepartmentSelectorBarSkeleton';
import {ProfileComparisonSection} from '../../../../features/company/workforce/components/ProfileComparison/ProfileComparisonSection';
import {ProfileComparisonSectionSkeleton} from '../../../../features/company/workforce/components/ProfileComparison/ProfileComparisonSectionSkeleton';
import {useDepartmentHealthDetail} from '../../../../features/company/workforce/hooks/useDepartmentHealthDetail';
import {useDepartmentHealthMetric} from '../../../../features/company/workforce/hooks/useDepartmentHealthMetric';

export const WorkforceDepartmentHealthPage = () => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDepartmentHealthPage'});
  const {company} = useContext(CompanyContext);
  const companyId = company.data?.resourceId ?? '';
  const {data: departments, isLoading, error} = useDepartmentHealthMetric(companyId);
  const [searchParams] = useSearchParams();
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | null>(null);
  const {
    data: healthDetail,
    isLoading: isHealthLoading,
    error: healthError,
  } = useDepartmentHealthDetail(companyId, selectedDepartmentId);

  const selectedDepartment = departments?.find((dept) => dept.companyDepartmentId === selectedDepartmentId);
  const departmentName = selectedDepartment?.companyDepartmentName ?? '';
  const departmentList = departments ?? [];
  const hasDepartments = departmentList.length > 0;

  useEffect(() => {
    const departmentIdFromQuery = searchParams.get('departmentId');
    if (!departmentIdFromQuery || !departments?.length) {
      return;
    }

    const matchingDepartment = departments.find((dept) => dept.companyDepartmentId === departmentIdFromQuery);
    if (matchingDepartment) {
      setSelectedDepartmentId(matchingDepartment.companyDepartmentId);
    }
  }, [departments, searchParams]);

  return (
    <Box sx={{maxWidth: 1120, mx: 'auto', width: '100%'}}>
      <ModulePageHeader moduleName={t('breadcrumb')} title={t('title')} />

      {isLoading ? (
        <DepartmentSelectorBarSkeleton />
      ) : error ? (
        <Box sx={{py: 1.5}}>
          <Typography variant='body2' color='error'>
            {t('selector.loadingError')}
          </Typography>
        </Box>
      ) : hasDepartments ? (
        <DepartmentSelectorBar
          departments={departmentList}
          selectedId={selectedDepartmentId}
          onSelect={setSelectedDepartmentId}
        />
      ) : null}

      <Box sx={{pt: 1, pb: 3}}>
        {!selectedDepartmentId ? (
          <EmptyStateCard message={String(t('emptyState.message'))} />
        ) : isHealthLoading ? (
          <>
            <DepartmentHealthNarrativeHeaderSkeleton />
            <DepartmentHealthScoreGrid loading />
            <DepartmentHealthRecommendedActions loading />
            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 2, mb: 2}}>
              <DepartmentHealthProfileDecisionGuide loading embedded />
              <DepartmentHealthRoleCoverageHealth loading embedded />
            </Box>
            <DepartmentHealthSkillCoverageMap loading />
            <DepartmentHealthBenchmarkTableSkeleton />
            <DepartmentHealthDataFreshness loading />
            <ProfileComparisonSectionSkeleton />
          </>
        ) : healthError ? (
          <Typography variant='body2' color='error'>
            {t('scoreCards.loadingError')}
          </Typography>
        ) : healthDetail ? (
          <>
            <DepartmentHealthNarrativeHeader departmentName={departmentName} health={healthDetail} />
            <DepartmentHealthScoreGrid scores={healthDetail.scores} benchmarks={healthDetail.benchmarks} />
            <DepartmentHealthRecommendedActions actionItems={healthDetail.actionItems} />
            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 2, mb: 2}}>
              <DepartmentHealthProfileDecisionGuide profileDecisionGuide={healthDetail.profileDecisionGuide} embedded />
              <DepartmentHealthRoleCoverageHealth roleCoverage={healthDetail.roleCoverage} embedded />
            </Box>
            <DepartmentHealthSkillCoverageMap skillCoverage={healthDetail.skillCoverage} />
            <DepartmentHealthBenchmarkTable
              departmentName={departmentName}
              scores={healthDetail.scores}
              benchmarks={healthDetail.benchmarks}
            />
            <DepartmentHealthDataFreshness dataFreshness={healthDetail.dataFreshness} />
            <ProfileComparisonSection
              companyId={companyId}
              departmentId={selectedDepartmentId}
              departmentName={departmentName}
            />
          </>
        ) : null}
      </Box>
    </Box>
  );
};
